import { forwardRef, ForwardedRef, useState, useEffect, useRef, useCallback } from 'react'

interface TextareaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
    label?: string
    inputId?: string
    error?: {
        message?: string // Allow message to be undefined
    },
    required?: boolean
    placeholder?: string
    maxLength?: number
    showCharacterCount?: boolean
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ label, inputId, error, required = false, placeholder, maxLength, showCharacterCount = false, defaultValue, ...props }, ref: ForwardedRef<HTMLTextAreaElement>) => {
    const [charCount, setCharCount] = useState(0)
    const localRef = useRef<HTMLTextAreaElement | null>(null)

    // Combine the forwarded ref with our local ref, this is mainly for the businessDescription to keep it in sync for the character count
    // between Solo Trader and Limited Company
    const setRefs = useCallback((element: HTMLTextAreaElement | null) => {
        localRef.current = element
        if (typeof ref === 'function') {
            ref(element)
        } else if (ref) {
            ref.current = element
        }
    }, [ref])

    useEffect(() => {
        // Read the actual value from the textarea DOM element
        if (localRef.current) {
            setCharCount(localRef.current.value.length)
        }
    }, [defaultValue])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCharCount(e.target.value.length)
        if (props.onChange) {
            props.onChange(e)
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (maxLength) {
            const pastedText = e.clipboardData.getData('text')
            const currentText = e.currentTarget.value
            const selectionStart = e.currentTarget.selectionStart
            const selectionEnd = e.currentTarget.selectionEnd
            
            // Calculate what the new text would be
            const textBeforeSelection = currentText.substring(0, selectionStart)
            const textAfterSelection = currentText.substring(selectionEnd)
            const newText = textBeforeSelection + pastedText + textAfterSelection
            
            // If the new text exceeds maxLength, truncate it
            if (newText.length > maxLength) {
                e.preventDefault()
                const availableSpace = maxLength - textBeforeSelection.length - textAfterSelection.length
                const truncatedText = pastedText.substring(0, availableSpace)
                const finalText = textBeforeSelection + truncatedText + textAfterSelection
                
                e.currentTarget.value = finalText
                setCharCount(finalText.length)
                
                // Trigger onChange manually since we prevented default
                if (props.onChange) {
                    const syntheticEvent = {
                        ...e,
                        target: e.currentTarget,
                        currentTarget: e.currentTarget
                    } as React.ChangeEvent<HTMLTextAreaElement>
                    props.onChange(syntheticEvent)
                }
            }
        }
        
        if (props.onPaste) {
            props.onPaste(e)
        }
    }

    const charsLeft = maxLength ? maxLength - charCount : 0

    return (
        <div>
            <label
                htmlFor={inputId}
                className={`
                    ${error ? 'text-[var(--theme-danger)]' : 'text-[var(--theme-text)]'} block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)] h-6 leading-4 text-pretty ${required ? 'after:ml-1.5 after:content-["*"]' : ''}
                    `}
                >
                {label}
            </label>
            <div className='mt-2 relative'>
                <textarea
                    ref={setRefs}
                    {...props}
                    id={inputId}
                    placeholder={placeholder}
                    rows={6}
                    maxLength={maxLength}
                    defaultValue={defaultValue}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    className={`
                        transition duration-500 appearance-none
                        resize-none block w-full rounded-md bg-white px-3 py-3 text-base outline outline-1
                        placeholder:text-[var(--input-placeholder)]
                        ${error ? 'outline-[var(--theme-danger)] focus:outline-[var(--theme-danger)]' : 'outline-[var(--input-border)] focus:outline-[var(--theme-text)]'}
                        focus:outline-1 sm:text-lg/6
                    `}
                />
                {showCharacterCount && maxLength && (
                    <div className='absolute bottom-2 right-3 text-xs text-gray-500 pointer-events-none'>
                        {charsLeft} character{charsLeft !== 1 ? 's' : ''} left
                    </div>
                )}
            </div>
            {error && error.message && <p className='p-0.5 text-md  text-[var(--theme-danger)]'>{error.message}</p>}
        </div>
    )
})

export default Textarea
